import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInvitationPageLocale } from '../hooks/useInvitationPageLocale';
import { getInvitationById, updateAttendanceStatus, addGiftToInvitation } from '../services/invitationService';
import { Invitation, AttendanceStatus } from '../types/Invitation';
import '../styles/InvitationPage.css';
import { getInvitationCardDownloadURL } from '../services/storageService';
import googleMapsIcon from '../resources/google-maps_icon.png';

const formatEventDateTime = (dateTime?: string): string => {
    if (!dateTime) return '';
    const d = new Date(dateTime);
    if (Number.isNaN(d.getTime())) return dateTime; // fallback to raw string if not parseable
    try {
        return new Intl.DateTimeFormat('ar-AR', {
            dateStyle: 'full',
            timeStyle: 'short',
        }).format(d);
    } catch {
        return d.toLocaleString();
    }
};

const buildGoogleMapsUrl = (params: {
    latitude?: number;
    longitude?: number;
    address?: string;
    name?: string;
}): string => {
    const { latitude, longitude, address, name } = params;
    const hasCoords =
        typeof latitude === 'number' &&
        typeof longitude === 'number' &&
        !Number.isNaN(latitude) &&
        !Number.isNaN(longitude) &&
        (latitude !== 0 || longitude !== 0);

    if (hasCoords) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`;
    }

    const q = (address || name || '').trim();
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

const buildWazeUrl = (params: {
    latitude?: number;
    longitude?: number;
    address?: string;
    name?: string;
}): string => {
    const { latitude, longitude, address, name } = params;
    const hasCoords =
        typeof latitude === 'number' &&
        typeof longitude === 'number' &&
        !Number.isNaN(latitude) &&
        !Number.isNaN(longitude) &&
        (latitude !== 0 || longitude !== 0);

    if (hasCoords) {
        return `https://waze.com/ul?ll=${encodeURIComponent(`${latitude},${longitude}`)}&navigate=yes`;
    }

    const q = (address || name || '').trim();
    return `https://waze.com/ul?q=${encodeURIComponent(q)}&navigate=yes`;
};

const WAZE_ICON_URL =
    'https://web-assets.waze.com/livemap/stg/app-driver-af88bd646b4c4f9fa810b33bd4c9be12.svg';

const InvitationPage: React.FC = () => {
    useInvitationPageLocale();

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [invitation, setInvitation] = useState<Invitation | null>(null);
    const [invitationCardDownloadURL, setInvitationCardDownloadURL] = useState<string | null>(null);
    const [attendance, setAttendance] = useState<AttendanceStatus | null>(null);
    const [showGiftModal, setShowGiftModal] = useState(false);
    const [showCardModal, setShowCardModal] = useState(false);
    const [giftAmount, setGiftAmount] = useState<string>('');
    const [giftNote, setGiftNote] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchInvitation = async () => {
            if (!id) {
                setLoading(false);
                return;
            }

            try {
                const foundInvitation = await getInvitationById(id);
                setInvitation(foundInvitation);
                const invitationCardDownloadURL = await getInvitationCardDownloadURL(foundInvitation?.weddingDetails?.invitationCardPath || '');
                setInvitationCardDownloadURL(invitationCardDownloadURL);
                setAttendance(foundInvitation?.attendanceStatus || null);
            } catch (err) {
                console.error('Error fetching invitation:', err);
                setError('حدث خطأ أثناء تحميل الدعوة');
            } finally {
                setLoading(false);
            }
        };

        fetchInvitation();
    }, [id]);

    useEffect(() => {
        if (!showCardModal) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowCardModal(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [showCardModal]);

    const handleAttendanceChange = async (willAttend: boolean) => {
        if (!id || updating) return;

        const newStatus: AttendanceStatus = willAttend ? 'attending' : 'not_attending';
        setUpdating(true);

        try {
            await updateAttendanceStatus(id, newStatus);
            setAttendance(newStatus);
        } catch (err) {
            console.error('Error updating attendance:', err);
            alert('حدث خطأ أثناء تحديث حالة الحضور');
        } finally {
            setUpdating(false);
        }
    };

    const handleSendGift = async () => {
        if (!id || !giftAmount || parseFloat(giftAmount) <= 0) return;

        try {
            const newGift = {
                amount: giftAmount,
                note: giftNote?.trim() || '',
            };

            await addGiftToInvitation(id, newGift);

            // Update local UI immediately (no refresh needed)
            setInvitation((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    guest: {
                        ...prev.guest,
                        gifts: [...(prev.guest.gifts || []), newGift],
                    },
                };
            });

            alert(`شكراً لك! تم تسجيل نقوطك بقيمة ₪${giftAmount}`);
            setShowGiftModal(false);
            setGiftAmount('');
            setGiftNote('');
        } catch (err) {
            console.error('Error sending gift:', err);
            alert('حدث خطأ أثناء إرسال النقوط');
        }
    };

    if (loading) {
        return (
            <div className="invitation-loading">
                <div className="loader"></div>
                <p>جاري تحميل الدعوة...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="invitation-not-found">
                <h2>{error}</h2>
                <button onClick={() => navigate('/')}>العودة للرئيسية</button>
            </div>
        );
    }

    if (!invitation) {
        return (
            <div className="invitation-not-found">
                <h2>عذراً، الدعوة غير موجودة</h2>
                <button onClick={() => navigate('/')}>العودة للرئيسية</button>
            </div>
        );
    }

    const { guest, weddingDetails } = invitation;
    const guestFullName = `${guest.firstName} ${guest.middleName} ${guest.lastName}`.trim();
    
    // Wedding couple names
    const groomFullName = weddingDetails 
        ? `${weddingDetails.groomFirstName} ${weddingDetails.groomMiddleName} ${weddingDetails.groomLastName}`.trim()
        : '';
    const brideFullName = weddingDetails
        ? `${weddingDetails.brideFirstName} ${weddingDetails.brideMiddleName} ${weddingDetails.brideLastName}`.trim()
        : '';

    const weddingEvent = weddingDetails?.weddingEvent;
    const eventTitle = weddingEvent?.title?.trim() || '';
    const eventDateTime = formatEventDateTime(weddingEvent?.date_time);
    const eventLocationName = weddingEvent?.location?.name?.trim() || '';
    const eventLocationAddress = weddingEvent?.location?.address?.trim() || '';
    const eventDescription = weddingEvent?.description?.trim() || '';
    const eventLocationLat = weddingEvent?.location?.latitude;
    const eventLocationLng = weddingEvent?.location?.longitude;

    const googleMapsUrl = buildGoogleMapsUrl({
        latitude: eventLocationLat,
        longitude: eventLocationLng,
        address: eventLocationAddress,
        name: eventLocationName,
    });
    const wazeUrl = buildWazeUrl({
        latitude: eventLocationLat,
        longitude: eventLocationLng,
        address: eventLocationAddress,
        name: eventLocationName,
    });

    return (
        <div className="invitation-page">
            {/* Header */}
            <div className="invitation-header">
                <h1 className="invitation-title">دعوة زفاف</h1>
            </div>

            {/* Guest Welcome Section */}
            <div className="guest-welcome">
                <h2>أهلاً {guestFullName}</h2>
                <p className="welcome-message">يسعدنا دعوتك لمشاركتنا أجمل لحظات حياتنا</p>
            </div>

            {/* Wedding Card Image */}
            {weddingDetails && (
                <div className="invitation-card">
                    {weddingDetails.invitationCardPath && (
                        <img
                            src={invitationCardDownloadURL || ''}
                            alt="بطاقة دعوة الزفاف"
                            className="card-image"
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowCardModal(true)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') setShowCardModal(true);
                            }}
                        />
                    )}
                    <div className="card-overlay">
                        {!!weddingEvent?.title && (
                            <div className="event-title">
                                {weddingEvent.title}
                            </div>
                        )}
                        <div className="couple-names">
                            <h2>{brideFullName}</h2>
                            <div className="heart-divider">&</div>
                            <h2>{groomFullName}</h2>
                        </div>
                    </div>
                </div>
            )}

            {/* Wedding Details */}
            {weddingDetails && (
                <div className="wedding-details-section">
                    <h3>تفاصيل الحفل</h3>
                    {!!eventTitle && (
                        <div className="wedding-event-title">{eventTitle}</div>
                    )}
                    {!!eventDescription && (
                        <p className="wedding-event-description">{eventDescription}</p>
                    )}
                    <div className="details-grid">
                        <div className="detail-item">
                            <div className="detail-icon-wrapper">
                                <svg className="detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">الموعد</span>
                                <span className="detail-value">{eventDateTime || '—'}</span>
                            </div>
                        </div>

                        <div className="detail-item">
                            <div className="detail-icon-wrapper">
                                <svg className="detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                    <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">المكان</span>
                                <span className="detail-value">{eventLocationName || '—'}</span>
                                {!!eventLocationAddress && (
                                    <span className="detail-address">{eventLocationAddress}</span>
                                )}
                                {(!!eventLocationName || !!eventLocationAddress) && (
                                    <div className="map-actions">
                                        <a
                                            className="map-action-btn"
                                            href={googleMapsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="فتح الموقع في خرائط Google"
                                        >
                                            <img
                                                className="map-action-icon"
                                                src={googleMapsIcon}
                                                alt=""
                                                aria-hidden="true"
                                            />
                                            <span className="map-action-label">Google Maps</span>
                                        </a>
                                        <a
                                            className="map-action-btn secondary"
                                            href={wazeUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="فتح الموقع في Waze"
                                        >
                                            <img
                                                className="map-action-icon map-action-icon--waze"
                                                src={WAZE_ICON_URL}
                                                alt=""
                                                aria-hidden="true"
                                            />
                                            <span className="map-action-label">Waze</span>
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="detail-item">
                            <div className="detail-icon-wrapper">
                                <svg className="detail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                </svg>
                            </div>
                            <div className="detail-content">
                                <span className="detail-label">للتواصل</span>
                                <span className="detail-value" dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                                    {weddingDetails.phoneNumber}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Attendance Status */}
            <div className="attendance-section">
                <h3>حالة الحضور</h3>
                <p className="attendance-prompt">هل ستتمكن من الحضور؟</p>
                <div className="attendance-buttons">
                    <button
                        className={`attendance-btn ${attendance === 'attending' ? 'active yes' : ''}`}
                        onClick={() => handleAttendanceChange(true)}
                        disabled={updating}
                    >
                        <span className="btn-icon">✓</span>
                        {updating ? 'جاري التحديث...' : 'سأحضر بإذن الله'}
                    </button>
                    <button
                        className={`attendance-btn ${attendance === 'not_attending' ? 'active no' : ''}`}
                        onClick={() => handleAttendanceChange(false)}
                        disabled={updating}
                    >
                        <span className="btn-icon">✗</span>
                        {updating ? 'جاري التحديث...' : 'أعتذر عن الحضور'}
                    </button>
                </div>
                {attendance === 'attending' && (
                    <div className="attendance-confirmed">
                        <p>رائع! نتطلع لرؤيتك في حفلنا</p>
                    </div>
                )}
                {attendance === 'not_attending' && (
                    <div className="attendance-declined">
                        <p>نتفهم ظروفك، وسنكون سعداء بوجودك معنا في القلب</p>
                    </div>
                )}
                {attendance === 'pending' && (
                    <div className="attendance-pending">
                        <p>في انتظار ردك الكريم</p>
                    </div>
                )}
            </div>

            {/* Gift Section */}
            <div className="gift-section">
                <div className="gift-header">
                    <h3>أرسل النقوط</h3>
                </div>
                <p className="gift-description">
                    يمكنك إرسال النقوط إلكترونياً بكل سهولة وأمان
                </p>
                <button
                    className="send-gift-btn"
                    onClick={() => setShowGiftModal(true)}
                >
                    إرسال النقوط
                </button>
                
                {/* Display existing gifts */}
                {guest.gifts && guest.gifts.length > 0 && (
                    <div className="previous-gifts">
                        <p className="gifts-label">نقوطك:</p>
                        {guest.gifts.map((gift, index) => (
                            <div key={index} className="gift-item">
                                <span className="gift-amount">₪{gift.amount}</span>
                                {gift.note && <span className="gift-note">{gift.note}</span>}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add to Calendar Button */}
            <div className="calendar-section">
                <button className="calendar-btn">
                    <svg className="calendar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    أضف للتقويم
                </button>
            </div>

            {/* Gift Modal */}
            {showGiftModal && (
                <div className="modal-overlay" onClick={() => setShowGiftModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close"
                            onClick={() => setShowGiftModal(false)}
                        >
                            ✕
                        </button>
                        <h3>إرسال النقوط</h3>
                        <p className="modal-description">
                            شكراً لكرمك! قيمة النقوط ستصل مباشرة للعروسين
                        </p>

                        <div className="gift-amount-section">
                            <label>المبلغ (شاقل)</label>
                            <input
                                type="number"
                                placeholder="أدخل المبلغ"
                                value={giftAmount}
                                onChange={(e) => setGiftAmount(e.target.value)}
                                className="gift-input"
                                min="0"
                            />
                        </div>

                        <div className="gift-note-section">
                            <label>ملاحظة (اختياري)</label>
                            <input
                                type="text"
                                placeholder="أضف ملاحظة مع نقوطك"
                                value={giftNote}
                                onChange={(e) => setGiftNote(e.target.value)}
                                className="gift-input"
                            />
                        </div>

                        <div className="suggested-amounts">
                            <button onClick={() => setGiftAmount('100')}>₪100</button>
                            <button onClick={() => setGiftAmount('200')}>₪200</button>
                            <button onClick={() => setGiftAmount('500')}>₪500</button>
                            <button onClick={() => setGiftAmount('1000')}>₪1000</button>
                        </div>

                        <button
                            className="confirm-gift-btn"
                            onClick={handleSendGift}
                            disabled={!giftAmount || parseFloat(giftAmount) <= 0}
                        >
                            تأكيد الإرسال
                        </button>
                    </div>
                </div>
            )}

            {/* Invitation Card Fullscreen Modal */}
            {showCardModal && (
                <div className="modal-overlay" onClick={() => setShowCardModal(false)}>
                    <div className="card-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close"
                            onClick={() => setShowCardModal(false)}
                            aria-label="إغلاق"
                        >
                            ✕
                        </button>
                        <img
                            src={invitationCardDownloadURL || ''}
                            alt="بطاقة دعوة الزفاف - عرض كامل"
                            className="card-modal-image"
                        />
                    </div>
                </div>
            )}

            {/* Footer Message */}
            <div className="invitation-footer">
                <p className="footer-message">
                    ننتظركم بفارغ الصبر لمشاركتنا فرحتنا
                </p>
            </div>
        </div>
    );
};

export default InvitationPage;
